// ARIA Unified Intent Router — Preview pilot (2026-02).
//
// KISS design (per user directive): four intents, priority-ordered, pure
// client-side, no backend classifier, no ML. The backend `/api/ask` remains
// authoritative for PHI, urgent-event, and numerical safety checks — the
// router simply decides where an utterance goes.
//
// Priority order (first match wins):
//   1. CAMERA   — explicit photo / assessment language (beats nav verbs)
//   2. QA       — clear question form (leader word OR trailing '?')
//   3. NAV      — nav-verb-led utterance that matches an ARIA_COMMANDS entry
//   4. QA       — clinical anchor with enough surrounding context (>= 4 words)
//   5. NAV      — short (≤ 6 words) match against ARIA_COMMANDS, no anchor
//   6. CLARIFY  — nothing else fired
//
// This file is gated at the call-site by REACT_APP_UNIFIED_ROUTER=1. When
// the flag is off, ARIA falls back to the existing runCommand nav-only path.

import { matchCommand } from "./aria";

export const ROUTER_INTENTS = Object.freeze({
  QA: "qa",
  CAMERA: "camera",
  NAV: "nav",
  CLARIFY: "clarify",
});

// ---- Photo / assessment anchors (rule 1). Must beat generic nav verbs. ----
// Deliberately narrow: "camera", "photo", "picture", "image", "radiograph",
// "xray", "scan of", "pan/CBCT". Also the explicit brand phrases
// "quick assessment" and "photo assessment" so the clinician doesn't have to
// memorise them but they still win over "show" / "open" nav intents.
const PHOTO_ANCHORS = [
  /\bphoto\s+assessment\b/i,
  /\bquick\s+assessment\b/i,
  /\bopen\s+(?:the\s+|my\s+)?camera\b/i,
  /\bstart\s+(?:the\s+|my\s+)?camera\b/i,
  /\blaunch\s+(?:the\s+|my\s+)?camera\b/i,
  /\btake\s+(?:a\s+)?(?:photo|picture|pic|snapshot|snap)\b/i,
  /\bassess\s+(?:this|the|these|that)\s+(?:photo|image|picture|radiograph|x-?ray|scan|pan(?:orex)?|cbct)\b/i,
  /\blook\s+at\s+(?:this|the|these|that)\s+(?:photo|image|picture|radiograph|x-?ray|scan|pan(?:orex)?|cbct)\b/i,
  /\breview\s+(?:this|the|these|that)\s+(?:photo|image|picture|radiograph|x-?ray|scan|pan(?:orex)?|cbct)\b/i,
  /\bshow\s+me\s+(?:a\s+|the\s+)?photo\s+assessment\b/i,
  /\bphoto\s+of\s+(?:the|this|that|a|an)\b/i,
  /\b(?:intraoral|panoramic|periapical|bitewing)\s+(?:photo|image|radiograph|x-?ray|scan)\b/i,
];

// ---- Question form (rule 2). Interrogative lead words + trailing '?'. ----
const QUESTION_LEADERS = /^(?:what|when|where|which|why|how|who|whom|whose|is|are|am|was|were|should|shall|can|could|would|does|do|did|may|might|will|have|has|had)\b/i;
const QUESTION_MARK = /\?\s*$/;

// ---- Navigation-verb lead (rule 3). Short list, first-token only. ----
const NAV_VERB_LEAD = /^(?:open|go\s+to|take\s+me\s+to|show|display|switch\s+to|back\s+to|close|hide|navigate\s+to|bring\s+up|pull\s+up)\b/i;

// ---- Clinical anchor set (rule 4). Compact, high-signal vocabulary. ----
// Kept intentionally short — extending it is safe (only widens QA routing).
// The word-count gate (>= 4 words) suppresses bare noun-phrase misfires like
// "Nobel Chronicle" which the user's spec requires to hit CLARIFY.
const CLINICAL_ANCHORS = new RegExp(
  "\\b(?:" +
    // Restorative / prostho
    "abutment|torque|isq|ncm|zirconia|e-?max|lithium\\s+disilicate|feldspathic|" +
    "crown|bridge|onlay|inlay|veneer|denture|rpd|hybrid|all-?on-?(?:x|four|4|six|6)|" +
    "provisional|cement(?:ation|ed)?|bond(?:ing|ed)?|articulator|centric|" +
    "occlus(?:ion|al)|bruxism|tmd|vdo|" +
    // Perio / endo / surgery
    "perio(?:dontal|dontitis)?|endo(?:dontic)?|apical|resorption|osseointegrat|" +
    "graft|sinus\\s+lift|extraction|socket|ridge\\s+preservation|cortical|" +
    "keratinized|attached\\s+gingiva|papilla|mucosa|" +
    // Implant systems (common)
    "implant|nobel|straumann|neodent|bicon|zimmer|astra|megagen|hiossen|dentsply|" +
    "biohorizons|osstem|blx|tissue-?level|bone-?level|cover\\s+screw|healing\\s+abutment|" +
    "screw-?retained|cement-?retained|scan\\s+body|verification\\s+jig|" +
    // Prep / margin
    "margin|prep|preparation|reduction|emergence\\s+profile|" +
    "distal|mesial|buccal|palatal|lingual|occlusal|gingival|" +
    // Imaging / diagnostics
    "radiograph|panoramic|periapical|bitewing|cbct|cone-?beam|" +
    "impression|final\\s+impression|verification|primary\\s+stability|" +
    // Anatomy / conditions relevant to the pilot
    "bone\\s+loss|open\\s+margin|open\\s+contact|open\\s+crown\\s+margin|" +
    "fractured\\s+cusp|resorption|apex" +
  ")\\b",
  "i"
);

function _wordCount(t) {
  return (t.trim().match(/\S+/g) || []).length;
}

/**
 * classifyIntent(transcript, opts) -> { intent, confidence, navCommand? }
 *
 * Pure function. Does NOT call the backend, does NOT persist anything, does
 * NOT touch the DOM. `opts.isAdmin` is forwarded to matchCommand so
 * admin-only nav vocabulary is respected.
 */
export function classifyIntent(transcript, { isAdmin = false } = {}) {
  if (!transcript || typeof transcript !== "string") {
    return { intent: ROUTER_INTENTS.CLARIFY, confidence: "low" };
  }
  const t = transcript.trim();
  if (!t) return { intent: ROUTER_INTENTS.CLARIFY, confidence: "low" };

  // Rule 1 — Photo / assessment language wins over generic nav verbs.
  for (const pat of PHOTO_ANCHORS) {
    if (pat.test(t)) {
      return { intent: ROUTER_INTENTS.CAMERA, confidence: "high" };
    }
  }

  const wc = _wordCount(t);
  const hasQMark = QUESTION_MARK.test(t);
  const hasQLead = QUESTION_LEADERS.test(t);

  // Rule 2 — explicit question form → Clinical Q&A.
  // This beats nav verbs so "what causes an open crown margin?" is Q&A
  // even though it contains the token "open".
  if (hasQLead || hasQMark) {
    return { intent: ROUTER_INTENTS.QA, confidence: "high" };
  }

  const navLed = NAV_VERB_LEAD.test(t);
  const navCmd = matchCommand(t, { isAdmin });

  // Rule 3 — nav-verb-led AND matches known nav vocabulary → NAV.
  if (navLed && navCmd) {
    return { intent: ROUTER_INTENTS.NAV, confidence: "high", navCommand: navCmd };
  }

  // Rule 4 — clinical anchor present with enough context → Q&A.
  // The word-count gate keeps bare noun phrases ("Nobel Chronicle",
  // "torque") in CLARIFY where the user's spec puts them.
  const hasClinical = CLINICAL_ANCHORS.test(t);
  if (hasClinical && wc >= 4) {
    return { intent: ROUTER_INTENTS.QA, confidence: "medium" };
  }

  // Rule 5 — short utterance, no clinical anchor, matches nav vocabulary.
  // (Handles bare phrases like "dashboard", "review queue", "my patients"
  // that the existing matchCommand catches.)
  if (navCmd && wc <= 6 && !hasClinical) {
    return { intent: ROUTER_INTENTS.NAV, confidence: "medium", navCommand: navCmd };
  }

  // Rule 6 — nothing fired confidently → clarify.
  return { intent: ROUTER_INTENTS.CLARIFY, confidence: "low" };
}

// Short spoken confirmation strings (per user directive: ≤ 6 words each).
export const ROUTER_CONFIRMATIONS = Object.freeze({
  qa: "Checking Quick Assist.",
  camera: "Opening the camera.",
  // NAV uses `Opening ${label}.` from the caller so admin/module names match.
});

// Flag helper — safe if window/process are missing (SSR, tests).
export function isUnifiedRouterEnabled() {
  try {
    // eslint-disable-next-line no-undef
    return String(process.env.REACT_APP_UNIFIED_ROUTER || "").trim() === "1";
  } catch (_) {
    return false;
  }
}
